<template>
  <div class="pa-4">
    <div class="d-flex align-baseline">
      <v-icon>mdi-view-dashboard-edit</v-icon>
      <h3 class="ml-2">Dashboard Configuration</h3>
    </div>

    <v-skeleton-loader
      v-if="loading"
      type="list-item-avatar, list-item-two-line, divider, list-item-avatar, list-item-two-line, divider"
      :loading="loading"
      class="ma-8"
    />

    <v-list v-else dense color="grey lighten-5">
      <!-- cards -->
      <DashboardItemsConfigHeader
        :icon="headerInfo.card.icon"
        :title="headerInfo.card.title"
        :subtitle="headerInfo.card.subtitle"
      >
        <template #action>
          <v-switch v-model="accountDashboardConfig.showQuickInfo" />
        </template>
        <template v-if="accountDashboardConfig.showQuickInfo" #extra-config>
          <!-- cards layout -->
          <v-list-item class="mx-8">
            <v-list-item-title> Card Layout </v-list-item-title>
            <CardLayoutInstruction />

            <v-list-item-action>
              <v-btn-toggle
                v-model="accountDashboardConfig.cardLayout"
                mandatory
                dense
              >
                <v-btn
                  v-for="layout in DASHBOARD_LAYOUTS"
                  :key="layout.value"
                  :value="layout.value"
                  active-class="white--text primary font-weight-medium"
                  class="text-caption text-capitalize"
                  small
                >
                  {{ layout.text }}
                </v-btn>
              </v-btn-toggle>
            </v-list-item-action>
          </v-list-item>
          <v-divider class="mx-8" />

          <!-- card size -->
          <v-list-item class="mx-8">
            <v-list-item-title> Card Size </v-list-item-title>
            <CardSizeInstruction />
            <v-list-item-action>
              <v-btn-toggle
                v-model="accountDashboardConfig.cardSize"
                mandatory
                dense
                class="mr-8"
              >
                <v-btn
                  v-for="size in DASHBOARD_CARD_SIZES"
                  :key="size.value"
                  :value="size.value"
                  active-class="white--text primary font-weight-medium"
                  class="text-caption text-capitalize"
                  small
                >
                  {{ size.text }}
                </v-btn>
              </v-btn-toggle>
            </v-list-item-action>
          </v-list-item>
        </template>
      </DashboardItemsConfigHeader>
      <DashboardItemsConfigList
        v-if="accountDashboardConfig.showQuickInfo"
        :items="accountDashboardConfig.cards"
      />

      <!-- widgets -->
      <DashboardItemsConfigHeader
        :icon="headerInfo.widget.icon"
        :title="headerInfo.widget.title"
        :subtitle="headerInfo.widget.subtitle"
      />
      <DashboardItemsConfigList :items="accountDashboardConfig.widgets" />
    </v-list>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import {
  allDashboardWidgets,
  allDashboardCards,
} from "@/store/dashboardItems.js";
import {
  DASHBOARD_CARD_SIZES,
  DASHBOARD_LAYOUTS,
} from "@/store/util/dashboardUtils";
import DashboardItemsConfigList from "@/components/dashboardConfig/DashboardItemsConfigList.vue";
import DashboardItemsConfigHeader from "@/components/dashboardConfig/DashboardItemsConfigHeader.vue";
import CardLayoutInstruction from "@/components/dashboardConfig/CardLayoutInstruction.vue";
import CardSizeInstruction from "@/components/dashboardConfig/CardSizeInstruction.vue";
import {
  getAccount,
  getAccountDashboardConfig,
  updateAccountDashboardConfig,
} from "@/services/accountServices";
export default {
  name: "DashboardConfig",
  data() {
    return {
      DASHBOARD_CARD_SIZES,
      DASHBOARD_LAYOUTS,
      allDashboardWidgets,
      allDashboardCards,
      accountDashboardConfig: null,
      loading: false,
      accountId: null,
      headerInfo: {
        card: {
          title: "Cards",
          icon: "mdi-comment-flash-outline",
          subtitle:
            "Enable or disable dashboard cards to customize your dashboard view.",
        },
        widget: {
          title: "Widgets",
          icon: "mdi-view-dashboard",
          subtitle:
            "Enable or disable dashboard widgets to customize your dashboard view.",
        },
      },
    };
  },
  components: {
    DashboardItemsConfigList,
    DashboardItemsConfigHeader,
    CardLayoutInstruction,
    CardSizeInstruction,
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapActions("messages", ["addErrorMessage", "addSuccessMessage"]),
    async fetchData() {
      try {
        this.loading = true;
        const account = await getAccount();
        const config = await getAccountDashboardConfig(account.id);
        this.accountId = account.id;
        this.parseConfig(config);
      } catch (err) {
        this.addErrorMessage("Error fetching dashboard config from server");
      } finally {
        this.loading = false;
      }
    },
    parseConfig(config) {
      const mappedWidgets = this.allDashboardWidgets.map((widget) => ({
        ...widget,
        ...(config?.widgets?.find((w) => w.id === widget.id) || {
          active: false,
        }),
      }));
      const mappedCards = this.allDashboardCards.map((card) => ({
        ...card,
        ...(config?.cards?.find((w) => w.id === card.id) || { active: false }),
      }));

      this.accountDashboardConfig = {
        ...config,
        cards: mappedCards,
        widgets: mappedWidgets,
      };
    },
    async updateConfig() {
      const payload = {
        ...this.accountDashboardConfig,
        cards: this.accountDashboardConfig.cards.map((c) => ({
          id: c.id,
          active: c.active,
          positionIndex: c.positionIndex,
        })),
        widgets: this.accountDashboardConfig.widgets.map((w) => ({
          id: w.id,
          active: w.active,
          positionIndex: w.positionIndex,
          size: w.size,
        })),
      };
      await updateAccountDashboardConfig(this.accountId, payload);
    },
  },
  watch: {
    accountDashboardConfig: {
      handler(newVal, oldVal) {
        if (!oldVal) return;
        this.updateConfig();
      },
      deep: true,
      immediate: false,
    },
  },
};
</script>
